import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SelectButton } from 'primeng/selectbutton';
import { Textarea } from 'primeng/textarea';
import {
    AiAssistApiError,
    AiAssistService,
    AI_ASSIST_ICON,
    BaseArtCandidate,
    BaseArtResult,
    GenerateMode,
    VENDOR_FLUX,
} from '@/app/services/ai-assist.service';
import { BrandKitImage } from '@/app/services/brand-kit.service';
import { ImagePreviewService } from '@/app/services/image-preview.service';

/** Game base-art plate types the generator supports (no brand_icon — that is realm-level). */
export type GamePlateType = 'icon' | 'banner';

/** An operator's chosen candidate per asset type, returned via `(applied)`. */
export interface BaseArtPick {
    assetType: GamePlateType;
    url: string;
}

type Phase = 'form' | 'loading' | 'results';

/**
 * "Generate with AI" dialog for game base art (`icon` / `banner` plates). The
 * art is the game's spirit rendered in the chosen site template's style — style
 * descriptor + text-free references come from the template's brand-kit; the game
 * context (name / description / kind) is passed inline. Realm-independent
 * (game plates are keyed by game-template × website-template), so no realm is
 * sent. Generates one async job per requested asset type, polls them, and lets
 * the operator pick one candidate per type. Emits the chosen AI-bucket URLs via
 * `(applied)`; the parent re-hosts them into the shared content store.
 */
@Component({
    selector: 'app-ai-base-art-generate',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        Button,
        Checkbox,
        Dialog,
        Message,
        ProgressSpinner,
        SelectButton,
        Textarea,
    ],
    templateUrl: './ai-base-art-generate.html',
})
export class AiBaseArtGenerate {
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    readonly visible = input.required<boolean>();
    /** Which plates to generate — `['icon']`, `['banner']`, or both. */
    readonly assetTypes = input<readonly GamePlateType[]>([]);

    readonly gameTemplateId = input<string>('');
    readonly gameKind = input<string>('');
    readonly gameName = input<string>('');
    readonly gameDescription = input<string>('');

    /** Website-template id + style version the plate is styled to (from the brand-kit). */
    readonly templateId = input<string>('');
    readonly version = input<string>('');
    readonly references = input<readonly BrandKitImage[]>([]);
    readonly style = input<Record<string, unknown> | null>(null);

    readonly visibleChange = output<boolean>();
    readonly applied = output<BaseArtPick[]>();

    protected readonly aiIcon = AI_ASSIST_ICON;

    protected readonly phase = signal<Phase>('form');
    protected readonly error = signal<string | null>(null);

    protected readonly samples = signal(4);
    protected readonly sampleOptions = [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
    ];
    /**
     * Form prompt box. Empty = pure auto. With text it is semi-auto by default
     * (folded into Grok as `operator_note`); `fullManual` flips it to a complete
     * prompt that replaces authoring.
     */
    protected readonly prompt = signal('');
    protected readonly fullManual = signal(false);

    /**
     * Refine box on the results screen — same semi-auto-by-default rule, governed
     * by `regenFullManual`. Empty = re-roll with the same settings.
     */
    protected readonly regenPrompt = signal('');
    protected readonly regenFullManual = signal(false);

    /**
     * Single-asset Grok-authored prompt from the last run — prefilled into the
     * refine box when the operator switches it to full-manual to edit it.
     */
    private readonly authoredPrompt = signal<string | null>(null);

    /** assetType -> generated candidates. */
    protected readonly results = signal<Record<string, BaseArtCandidate[]>>({});
    /** assetType -> the selected candidate url. */
    protected readonly selected = signal<Record<string, string>>({});

    private abort: AbortController | null = null;

    constructor() {
        // Reset the form each time the dialog opens; probe service health + credits
        // so the gating reflects reality. Cancel any polling when it closes.
        effect(() => {
            if (this.visible()) {
                this.reset();
                if (this.aiAssist.status() === 'unknown') {
                    void this.aiAssist.refreshHealth();
                }
                void this.aiAssist.refreshVendorBalances();
            } else {
                this.cancelPolling();
            }
        });
    }

    protected readonly header = computed(() => {
        const types = this.assetTypes();
        return types.length === 1
            ? `AI — ${types[0]} base art`
            : 'AI — icon + banner base art';
    });

    /** Non-null when FLUX (the only vendor game plates use) is out of credits. */
    protected readonly creditBlock = computed<string | null>(() =>
        this.aiAssist.blockedVendors().includes(VENDOR_FLUX)
            ? 'Generation paused — FLUX is out of credits. Top up the vendor account to continue.'
            : null,
    );

    /** Core submit gate shared by Generate (form) and Regenerate (results). */
    private canSubmit(text: string, fullManual: boolean): boolean {
        if (this.assetTypes().length === 0) return false;
        const refs = this.references().length;
        if (refs < 1 || refs > 8) return false;
        if (!this.templateId().trim() || !this.version().trim()) return false;
        if (!this.gameName().trim()) return false;
        if (fullManual && !text.trim()) return false;
        if (this.creditBlock()) return false;
        return true;
    }

    protected readonly canGenerate = computed<boolean>(() =>
        this.canSubmit(this.prompt(), this.fullManual()),
    );
    protected readonly canRegenerate = computed<boolean>(() =>
        this.canSubmit(this.regenPrompt(), this.regenFullManual()),
    );

    protected readonly canApply = computed<boolean>(() => {
        const sel = this.selected();
        return this.assetTypes().every((t) => !!sel[t]);
    });

    protected onVisibleChange(value: boolean): void {
        if (!value) this.cancelPolling();
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    protected generate(): void {
        void this.runGeneration(
            this.promptArgs(this.prompt(), this.fullManual()),
        );
    }

    /** Map a (text, fullManual) pair to the request's prompt fields. */
    private promptArgs(
        text: string,
        fullManual: boolean,
    ): {
        mode: GenerateMode;
        prompt: string | null;
        operatorNote: string | null;
    } {
        const t = text.trim();
        if (fullManual)
            return { mode: 'manual', prompt: t, operatorNote: null };
        if (t) return { mode: 'auto', prompt: null, operatorNote: t };
        return { mode: 'auto', prompt: null, operatorNote: null };
    }

    private async runGeneration(args: {
        mode: GenerateMode;
        prompt: string | null;
        operatorNote: string | null;
    }): Promise<void> {
        if (this.phase() === 'loading') return;
        this.cancelPolling();
        this.abort = new AbortController();
        this.phase.set('loading');
        this.error.set(null);
        this.results.set({});
        this.selected.set({});
        const signal = this.abort.signal;

        try {
            const types = [...this.assetTypes()];
            const gameMeta = {
                name: this.gameName().trim(),
                description: this.gameDescription().trim(),
            };
            const refsB64 = this.references().map((r) => r.b64);

            const entries = await Promise.all(
                types.map(async (type) => {
                    const accepted = await this.aiAssist.generateBaseArt({
                        jobId: crypto.randomUUID(),
                        templateId: this.templateId().trim(),
                        version: this.version().trim(),
                        referenceImagesB64: refsB64,
                        assetType: type,
                        styleDescriptor: this.style(),
                        gameTemplateId: this.gameTemplateId(),
                        gameKind: this.gameKind(),
                        gameMeta,
                        mode: args.mode,
                        prompt: args.prompt,
                        operatorNote: args.operatorNote,
                        samples: this.samples(),
                    });
                    const done = await this.aiAssist.awaitJob(accepted.job_id, {
                        signal,
                    });
                    const result = done.result as BaseArtResult | null;
                    return {
                        type,
                        candidates: result?.candidates ?? [],
                        authored: result?.authored_prompt ?? null,
                    };
                }),
            );

            const map: Record<string, BaseArtCandidate[]> = {};
            for (const entry of entries) map[entry.type] = entry.candidates;
            this.results.set(map);
            // Keep the single-asset authored prompt so the operator can switch
            // the refine box to full-manual and edit it.
            this.authoredPrompt.set(
                this.assetTypes().length === 1
                    ? (entries[0]?.authored ?? null)
                    : null,
            );
            this.phase.set('results');
        } catch (err) {
            this.error.set(this.friendlyError(err));
            this.phase.set('form');
        }
    }

    protected candidatesFor(type: GamePlateType): BaseArtCandidate[] {
        return this.results()[type] ?? [];
    }

    protected isSelected(type: GamePlateType, url: string): boolean {
        return this.selected()[type] === url;
    }

    protected select(type: GamePlateType, url: string): void {
        this.selected.update((s) => ({ ...s, [type]: url }));
    }

    protected apply(): void {
        if (!this.canApply()) return;
        const sel = this.selected();
        const picks: BaseArtPick[] = this.assetTypes()
            .map((assetType) => ({ assetType, url: sel[assetType] }))
            .filter((p) => !!p.url);
        this.applied.emit(picks);
        this.onVisibleChange(false);
    }

    protected backToForm(): void {
        this.phase.set('form');
    }

    /**
     * Regenerate from the results screen. Empty box re-rolls with the same
     * settings; with text it is semi-auto by default (folded into Grok) or a
     * full replacement when `regenFullManual` is on.
     */
    protected regenerate(): void {
        void this.runGeneration(
            this.promptArgs(this.regenPrompt(), this.regenFullManual()),
        );
    }

    /** Switching the refine box to full-manual prefills it with the authored prompt. */
    protected onRegenFullManualChange(value: boolean): void {
        this.regenFullManual.set(value);
        const authored = this.authoredPrompt();
        if (value && authored && !this.regenPrompt().trim()) {
            this.regenPrompt.set(authored);
        }
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private reset(): void {
        this.cancelPolling();
        this.phase.set('form');
        this.error.set(null);
        this.results.set({});
        this.selected.set({});
        this.regenPrompt.set('');
        this.regenFullManual.set(false);
        this.authoredPrompt.set(null);
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service. Please try again.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long. Please try again.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable. Please try again shortly.';
            case 'rate_limited':
                return 'Too many requests right now. Please wait a moment and retry.';
            case 'content_moderated':
                return 'The request was blocked by the content filter. Try a different prompt or references.';
            case 'invalid_references':
                return 'One or more reference images could not be used (must be public, text-free images).';
            case 'unauthorized':
                return 'Your session is not authorized. Please sign in again.';
            case 'forbidden':
                return 'Only a global admin can generate base art.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected as invalid.';
            default:
                return err.message ?? 'Something went wrong. Please try again.';
        }
    }
}
